import { useMemo } from 'react';

const NutritionLabel = ({ data }) => {
  const {
    product_name,
    ingredients_text,
    allergen_text,
    serving_size_g = 50,
    servings_per_pack,
    nutrients = [],
    label_content = {}
  } = data;

  // Calculate per serve value
  const calculatePerServe = (per100g) => {
    return ((per100g * serving_size_g) / 100).toFixed(1);
  };

  // Calculate % RDA based on per serve value (not per 100g)
  const calculateRDAPerServe = (nutrient) => {
    if (!nutrient.show_rda || !nutrient.rda_percentage) return null;
    return Math.round(nutrient.rda_percentage * serving_size_g / 100);
  };

  // Default labels
  const labels = {
    serving_size_label: label_content.serving_size_label || "Serving Size",
    per_100g_label: label_content.per_100g_label || "Per 100g",
    per_serve_label: label_content.per_serve_label || "Per Serve",
    rda_label: label_content.rda_label || "% RDA*",
    servings_per_pack_label: label_content.servings_per_pack_label || "Servings per Pack",
    nutrient_label: label_content.nutrient_label || "Nutrient",
    footnote_text: label_content.footnote_text || "*Percent Recommended Daily Allowance (RDA) are based on a 2000 calorie diet. Your daily values may be higher or lower depending on your calorie needs.",
    ingredients_heading: label_content.ingredients_heading || "INGREDIENTS:",
    allergen_heading: label_content.allergen_heading || "ALLERGEN INFORMATION:"
  };

  // List of macro nutrients - entire row should be bold
  const macroNutrients = ['Energy', 'Protein', 'Carbohydrate', 'Dietary Fibre', 'Total Fat', 'Sodium'];

 // Memoize sorted nutrients for performance
const sortedNutrients = useMemo(() => {
  return [...nutrients]
    .filter(
      (nutrient) =>
        nutrient.name !== "Saturated Fat" &&
        nutrient.name !== "Trans Fat"
    )
    .sort((a, b) => a.order - b.order);
}, [nutrients]);
  
    const nutrientDisplayNames = {
    Energy: 'Energy (kcal)',
    'Total Fat': 'Total Fat (g)',
    Protein: 'Protein (g)',
    Carbohydrate: 'Carbohydrate (g)',
    'Dietary Fibre': 'Dietary Fibre (g)',
    Sodium: 'Sodium (mg)'
  };

  return (
    <div data-testid="nutrition-label" className="nutrition-label border-2 border-black bg-white p-4 text-black max-w-2xl mx-auto print:border-2 print:border-black">
      {/* Header */}
      <div className="border-b-8 border-black pb-2 mb-2">
        <h2 style={{ fontWeight: 900 }} className="text-2xl leading-none">Nutritional Information (Approx)</h2>
      </div>

      {/* Product Info */}
      <div className="mb-3 pb-3 border-b-2 border-black">
        <div className="text-sm">
          <p><span style={{ fontWeight: 600 }}>Product:</span> {product_name}</p>
        </div>
      </div>

      {/* Serving Info */}
      <div className="mb-2 text-sm space-y-1">
        <p>
  <span style={{ fontWeight: 600 }}>{labels.serving_size_label}:</span> {serving_size_g}g
  {servings_per_pack && (
    <span className="ml-4">
      <span style={{ fontWeight: 600 }}>{labels.servings_per_pack_label}:</span> {servings_per_pack}
    </span>
  )}
</p>
      </div>

      {/* Nutrition Table - 4 columns */}
      <div className="border-t-4 border-black">
        {/* Table Header */}
        <div className="grid grid-cols-[2fr,1fr,1fr,0.8fr] border-b-2 border-black bg-gray-100 text-xs py-2" style={{ fontWeight: 700 }}>
          <div className="px-2">{labels.nutrient_label}</div>
          <div className="px-2 text-right">{labels.per_100g_label}</div>
          <div className="px-2 text-right">{labels.per_serve_label} ({serving_size_g}g)</div>
          <div className="px-2 text-right">{labels.rda_label}</div>
        </div>

        {/* Table Rows */}
        {sortedNutrients.map((nutrient, index) => {
          const perServe = calculatePerServe(nutrient.value_per_100g);
          const rdaPerServe = calculateRDAPerServe(nutrient);
          const isLastRow = index === sortedNutrients.length - 1;
          const borderClass = isLastRow ? "border-b-4 border-black" : "border-b border-black";
          // Entire row should be bold for macro nutrients
          const isMacro = !nutrient.is_sub_nutrient && macroNutrients.includes(nutrient.name);
          const rowStyle = isMacro ? { fontWeight: 700 } : {};
          
          return (
            <div
              key={nutrient.id || `nutrient-${index}`}
              className={`grid grid-cols-[2fr,1fr,1fr,0.8fr] ${borderClass} py-1 text-sm ${nutrient.is_sub_nutrient ? 'text-gray-600' : ''}`}
              style={rowStyle}
            >
              <div className={`px-2 ${nutrient.is_sub_nutrient ? 'pl-6' : ''}`}>
  {nutrientDisplayNames[nutrient.name] || nutrient.name}
</div>
              <div className="px-2 text-right">
                {nutrient.value_per_100g.toFixed(1)}
              </div>
              <div className="px-2 text-right">
                {perServe}
              </div>
              <div className="px-2 text-right">
                {nutrient.show_rda && rdaPerServe !== null
                  ? `${rdaPerServe}%`
                  : '—'}
              </div>
            </div>
          );
        })}
      </div>

      {/* RDA Note */}
      <div className="mt-3 pt-2 border-t border-black">
        <p className="text-xs italic">{labels.footnote_text}</p>
      </div>

      {/* Ingredients */}
      <div className="mt-4 pt-3 border-t-2 border-black">
        <p className="text-xs mb-1" style={{ fontWeight: 700 }}>{labels.ingredients_heading}</p>
        <p className="text-xs leading-relaxed">{ingredients_text}</p>
      </div>

      {/* Allergen Warning */}
      {allergen_text && (
        <div className="mt-3 pt-2 border-t border-black">
          <p className="text-xs mb-1" style={{ fontWeight: 700 }}>{labels.allergen_heading}</p>
          <p className="text-xs leading-relaxed">{allergen_text}</p>
        </div>
      )}
    </div>
  );
};

export default NutritionLabel;
